/**
 * Utility Helper Functions
 */

import { TicketPriority, TicketStatus, TicketType } from '@/types';
import { formatDistanceToNow, format } from 'date-fns';

export function cn(...classes: (string | undefined | null | false)[]) {
  return classes.filter(Boolean).join(' ');
}

export function formatDate(date: string | Date): string {
  return format(new Date(date), 'MMM d, yyyy');
}

export function formatDateTime(date: string | Date): string {
  return format(new Date(date), 'MMM d, yyyy h:mm a');
}

export function formatRelativeTime(date: string | Date): string {
  return formatDistanceToNow(new Date(date), { addSuffix: true });
}

export function getPriorityColor(priority: TicketPriority): string {
  const colors = {
    [TicketPriority.CRITICAL]: 'bg-red-100 text-red-800 border-red-200',
    [TicketPriority.HIGH]: 'bg-orange-100 text-orange-800 border-orange-200',
    [TicketPriority.MEDIUM]: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    [TicketPriority.LOW]: 'bg-blue-100 text-blue-800 border-blue-200',
  };
  return colors[priority] || colors[TicketPriority.MEDIUM];
}

export function getStatusColor(status: TicketStatus): string {
  const colors = {
    [TicketStatus.NEW]: 'bg-purple-100 text-purple-800 border-purple-200',
    [TicketStatus.TRIAGED]: 'bg-blue-100 text-blue-800 border-blue-200',
    [TicketStatus.IN_PROGRESS]: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    [TicketStatus.WAITING_CUSTOMER]: 'bg-amber-100 text-amber-800 border-amber-200',
    [TicketStatus.WAITING_INTERNAL]: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    [TicketStatus.IN_REVIEW]: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    [TicketStatus.RESOLVED]: 'bg-green-100 text-green-800 border-green-200',
    [TicketStatus.CLOSED]: 'bg-gray-100 text-gray-800 border-gray-200',
    [TicketStatus.REOPENED]: 'bg-red-100 text-red-800 border-red-200',
    [TicketStatus.CANCELLED]: 'bg-gray-100 text-gray-600 border-gray-200',
  };
  return colors[status] || colors[TicketStatus.NEW];
}

export function getTypeIcon(type: TicketType): string {
  const icons = {
    [TicketType.EPIC]: '🎯',
    [TicketType.USER_STORY]: '📖',
    [TicketType.TASK]: '✓',
    [TicketType.BUG]: '🐛',
    [TicketType.FEATURE_REQUEST]: '💡',
    [TicketType.SUPPORT_TICKET]: '💬',
    [TicketType.ERROR_LOG]: '⚠️',
  };
  return icons[type] || '📄';
}

export function formatLabel(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function getSLAStatusColor(slaStatus?: string): string {
  if (!slaStatus) return '';
  const colors = {
    ok: 'text-green-600',
    warning: 'text-amber-600',
    breached: 'text-red-600',
  };
  return colors[slaStatus as keyof typeof colors] || '';
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
